/**
 * Audible feedback for warehouse scans — bell on a match, buzzer on a miss.
 * Synthesized with the Web Audio API rather than shipped as audio files, so
 * there's no asset to load/license and nothing to fetch over the network.
 */

let sharedContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  if (!sharedContext) sharedContext = new Ctor();
  return sharedContext;
}

function playTone(ctx: AudioContext, {
  frequency,
  type,
  startTime,
  duration,
  peakGain,
}: {
  frequency: number;
  type: OscillatorType;
  startTime: number;
  duration: number;
  peakGain: number;
}) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(peakGain, startTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + duration);
}

/** A short two-note chime — played when a handler's scan matches a sales-entered tracking number. */
export function playBell(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;
    playTone(ctx, { frequency: 1046.5, type: 'sine', startTime: now, duration: 0.35, peakGain: 0.25 });
    playTone(ctx, { frequency: 1568.0, type: 'sine', startTime: now + 0.1, duration: 0.35, peakGain: 0.2 });
  } catch {
    // Audio is a courtesy, not a requirement — never let it break the save flow.
  }
}

/** A short harsh double-buzz — played when a scan doesn't match any known sales tracking number. */
export function playBuzzer(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;
    playTone(ctx, { frequency: 180, type: 'sawtooth', startTime: now, duration: 0.2, peakGain: 0.3 });
    playTone(ctx, { frequency: 160, type: 'sawtooth', startTime: now + 0.22, duration: 0.2, peakGain: 0.3 });
  } catch {
    // Audio is a courtesy, not a requirement — never let it break the save flow.
  }
}
